import { omit } from 'lodash';
import { type AvatarColor } from 'stylus-ui/Avatar';

export type Color =
  | 'blue'
  | 'indigo'
  | 'darkindigo'
  | 'cyan'
  | 'emerald'
  | 'yellow'
  | 'orange'
  | 'rose'
  | 'fuchsia'
  | 'darkfuchsia'
  | 'violet'
  | 'slate'
  | 'pink'
  | 'sky'
  | 'lime';

export type ShadowColor = 'blue' | 'gray' | 'purple' | 'pink' | 'cyan' | 'green' | 'yellow';

export const gradientColors: Record<Color, string> = {
  blue: 'bg-blue-500',
  cyan: 'bg-cyan-400',
  emerald: 'bg-green-500',
  fuchsia: 'bg-fuchsia-400',
  darkfuchsia: 'bg-fuchsia-600',
  indigo: 'bg-indigo-500',
  darkindigo: 'bg-indigo-600',
  lime: 'bg-lime-500',
  orange: 'bg-orange-500',
  pink: 'bg-pink-500',
  rose: 'bg-rose-400',
  sky: 'bg-sky-500',
  slate: 'bg-slate-100',
  violet: 'bg-purple-300',
  yellow: 'bg-yellow-400',
};

export const gradientColorsAsHex: Record<Color, string> = {
  blue: '#4299E1',
  cyan: '#22D3EE',
  emerald: '#10B981',
  fuchsia: '#E879F9',
  indigo: '#667EEA',
  lime: '#84CC16',
  orange: '#F97316',
  pink: '#EC4899',
  rose: '#FB7185',
  sky: '#38BDF8',
  slate: '#F1F5F9',
  violet: '#C4B5FD',
  yellow: '#FBBF24',
  darkfuchsia: '#C026D3',
  darkindigo: '#4F46E5',
};

export const fromColors: Record<Color, string> = {
  blue: 'from-blue-400',
  indigo: 'from-indigo-500',
  cyan: 'from-cyan-400',
  emerald: 'from-green-400',
  yellow: 'from-yellow-400',
  orange: 'from-orange-400',
  rose: 'from-rose-400',
  fuchsia: 'from-fuchsia-400',
  violet: 'from-purple-300',
  slate: 'from-slate-100',
  pink: 'from-pink-500',
  sky: 'from-sky-500',
  lime: 'from-lime-500',
  darkfuchsia: 'from-fuchsia-600',
  darkindigo: 'from-indigo-600',
};

export const viaColors: Record<Color, string> = {
  blue: 'via-blue-500',
  indigo: 'via-indigo-500',
  cyan: 'via-cyan-400',
  emerald: 'via-green-400',
  yellow: 'via-yellow-400',
  orange: 'via-orange-400',
  rose: 'via-rose-400',
  fuchsia: 'via-fuchsia-400',
  violet: 'via-purple-300',
  slate: 'via-slate-100',
  pink: 'via-pink-500',
  sky: 'via-sky-500',
  lime: 'via-lime-500',
  darkfuchsia: 'via-fuchsia-600',
  darkindigo: 'via-indigo-600',
};

export const toColors: Record<Color, string> = {
  blue: 'to-blue-500',
  indigo: 'to-indigo-500',
  cyan: 'to-cyan-400',
  emerald: 'to-green-400',
  yellow: 'to-yellow-400',
  orange: 'to-orange-400',
  rose: 'to-rose-400',
  fuchsia: 'to-fuchsia-400',
  violet: 'to-purple-400',
  slate: 'to-slate-100',
  pink: 'to-pink-500',
  sky: 'to-sky-500',
  lime: 'to-lime-500',
  darkfuchsia: 'to-fuchsia-600',
  darkindigo: 'to-indigo-600',
};

export const fromColorsPale: Record<Color, string> = {
  blue: 'from-blue-200',
  indigo: 'from-indigo-300',
  cyan: 'from-cyan-200',
  emerald: 'from-green-200',
  yellow: 'from-yellow-200',
  orange: 'from-orange-200',
  rose: 'from-rose-200',
  fuchsia: 'from-fuchsia-200',
  violet: 'from-purple-200',
  slate: 'from-slate-100',
  pink: 'from-pink-200',
  sky: 'from-sky-200',
  lime: 'from-lime-200',
  darkfuchsia: 'from-fuchsia-300',
  darkindigo: 'from-indigo-300',
};

export const viaColorsPale: Record<Color, string> = {
  blue: 'via-blue-200',
  indigo: 'via-indigo-300',
  cyan: 'via-cyan-200',
  emerald: 'via-green-200',
  yellow: 'via-yellow-200',
  orange: 'via-orange-200',
  rose: 'via-rose-200',
  fuchsia: 'via-fuchsia-200',
  violet: 'via-purple-200',
  slate: 'via-slate-100',
  pink: 'via-pink-200',
  sky: 'via-sky-200',
  lime: 'via-lime-200',
  darkfuchsia: 'via-fuchsia-300',
  darkindigo: 'via-indigo-300',
};

export const toColorsPale: Record<Color, string> = {
  blue: 'to-blue-200',
  indigo: 'to-indigo-300',
  cyan: 'to-cyan-200',
  emerald: 'to-green-200',
  yellow: 'to-yellow-200',
  orange: 'to-orange-200',
  rose: 'to-rose-200',
  fuchsia: 'to-fuchsia-200',
  violet: 'to-purple-200',
  slate: 'to-slate-100',
  pink: 'to-pink-200',
  sky: 'to-sky-200',
  lime: 'to-lime-200',
  darkfuchsia: 'to-fuchsia-300',
  darkindigo: 'to-indigo-300',
};

export const randomizableGradientColors: Partial<Record<Color, string>> = omit(gradientColors, [
  'slate',
]);
export const allGradientColors: Array<Color> = Object.keys(gradientColors) as Array<Color>;
export const getGradientBackgroundColor = (color: Color) => `${gradientColors[color]}`;

export const avatarColors: Record<AvatarColor, string> = {
  orange: 'bg-orange-500',
  yellow: 'bg-yellow-500',
  lime: 'bg-lime-500',
  emerald: 'bg-green-500',
  cyan: 'bg-cyan-500',
  blue: 'bg-blue-500',
  violet: 'bg-purple-500',
  fuchsia: 'bg-fuchsia-500',
  rose: 'bg-rose-500',
  gray: 'bg-slate-50',
};

export const allAvatarColors: Array<AvatarColor> = Object.keys(avatarColors) as Array<AvatarColor>;
export const getAvatarColors = (color: AvatarColor) => `${avatarColors[color]}`;
